import { TitleBlockBorders } from "./TitleBlockBorders";
import { TitleBlockModal } from "./TitleBlockModal";
import { WebGLRendering } from "./WebGLRendering";
import { TitleBlockSizes } from "./TitleBlockSizes";
import {
  baseUrl,
  generateTitleBlockURL,
  uploadTitleBlockURL,
  generatePDF,
} from "../../Experience/World/config";
import debug from "debug";

const log = debug("app:image");

/**
 * Manages the lifecycle, rendering, and interaction of the Title Block component.
 * @category Components
 * @subcategory TitleBlock
 */
export class TitleBlock {
  /**
   * Constructs a new TitleBlock instance.
   * @param {Object} experience - The experience object containing shared application resources.
   */
  constructor(experience) {
    log("TitleBlock constructor called");
    this.experience = experience;
    this.camera = this.experience.camera.instance;
    this.canvas = document.getElementById("modelCanvas");
    this.eventHub = experience.eventHub; // Keep event-based architecture
    this.titleBlockBorders = new TitleBlockBorders(experience);
    this.titleBlockModal = new TitleBlockModal(experience); // Modal for handling UI
    this.webGlRendering = new WebGLRendering(experience); // WebGL for rendering images
    this.titleBlockSizes = new TitleBlockSizes(experience); // Size management
    this.isTitleBlockVisible = false;

    this.handleDownload = this.handleDownload.bind(this);
    this.closeTitleBlock = this.closeTitleBlock.bind(this);
    this.handleGlobalResize = this.handleGlobalResize.bind(this);

    this.init();
  }

  /**
   * Initializes event listeners for title block interactions.
   */
  init() {
    this.eventHub.on("titleblock", () => {
      log("Titleblock event detected");
      if (this.isTitleBlockVisible) {
        this.closeTitleBlock();
        this.eventHub.emit("lockEvents", false);
      } else {
        this.openTitleBlock();
        this.eventHub.emit("alignPlane");
        this.eventHub.emit("lockEvents", true);
      }
    });
  }

  cleanup() {
    log("Cleaning up TitleBlock...");
  
    // Remove event listeners to prevent memory leaks
    this.eventHub.off("titleblock", this.toggleTitleBlock);
    this.eventHub.off("modal:downloadRequested", this.handleDownload);
    this.eventHub.off("modal:close", this.closeTitleBlock);
    this.eventHub.off("globalResize", this.handleGlobalResize);
  
    // Cleanup child components
    if (this.titleBlockBorders) {
      this.titleBlockBorders.removeBorders();
      this.titleBlockBorders = null;
    }
  
    if (this.titleBlockModal) {
      this.titleBlockModal.hideModal();
      this.titleBlockModal = null;
    }
  
    if (this.webGlRendering) {
      this.webGlRendering = null;
    }
  
    if (this.titleBlockSizes) {
      this.titleBlockSizes = null;
    }
  
    // Reset instance variables
    this.isTitleBlockVisible = false;
    this.experience = null;
    this.camera = null;
    this.canvas = null;
    this.eventHub = null;
  
    log("TitleBlock cleanup completed.");
  }

  /**
   * Opens the title block and sets up the borders, modal, and event listeners.
   */
  openTitleBlock() {
    log("Opening titleblock");

    this.titleBlockBorders.init();
    const sizes = this.titleBlockSizes.htmlElementSizes();
    this.titleBlockBorders.createBorders(sizes, this.canvas);
    this.eventHub.on("globalResize", this.handleGlobalResize);
    this.titleBlockModal.openTitleModal(sizes);

    this.eventHub.on("modal:downloadRequested", this.handleDownload);
    this.eventHub.on("modal:close", this.closeTitleBlock);

    this.eventHub.emit("needsRender", "true");  // ✅ Trigger rendering
    this.isTitleBlockVisible = true;
}


  /**
   * Closes the title block and removes all associated borders and event listeners.
   */
  closeTitleBlock() {
    log("Closing titleblock");

    this.eventHub.off("modal:downloadRequested", this.handleDownload);
    this.eventHub.off("modal:close", this.closeTitleBlock);
    this.eventHub.off("globalResize", this.handleGlobalResize);

    this.titleBlockBorders.removeBorders();
    this.titleBlockModal.hideModal();

    this.isTitleBlockVisible = false;
}


  /**
   * Handles resizing the title block borders when the window is resized.
   */
  handleGlobalResize() {
    const sizes = this.titleBlockSizes.htmlElementSizes();
    this.titleBlockBorders.updateBorders(sizes);
    
    this.eventHub.emit("needsRender", "true");  // ✅ Ensure UI updates visually
}


  /**
   * Handles the download process for the title block.
   * Renders the image and prepares a PDF file for download.
   */
  async handleDownload() {
    try {
      this.eventHub.emit("alignPlane");
        this.titleBlockModal.saveTitleblockModal(false);
        const sizes = this.titleBlockSizes.htmlElementSizes();
        log("sizes in world units", sizes);

        this.eventHub.emit("needsRender", "true");  // ✅ Ensure it's properly rendered before download

        const { blob } = await this.webGlRendering.getImage(sizes);

        console.log("blob", blob);
        if (blob) {
            const downloadUrl = await this.uploadScreenshotAndPreparePDF({blob});
            console.log("downloadUrl", downloadUrl);
            this.titleBlockModal.showDownloadLink(downloadUrl);
        }
    } catch (error) {
        log("Error during download process", error);
    }
}


  /**
   * Uploads the rendered screenshot and generates a PDF for download.
   * @param {Object} params - The parameters for uploading and generating the PDF.
   * @param {Blob} params.blob - The rendered image blob.
   * @param {string} params.url - The URL of the rendered image.
   * @returns {Promise<string>} A promise resolving to the URL of the generated PDF.
   * @throws {Error} If any upload or generation step fails.
   */
  async uploadScreenshotAndPreparePDF({ blob }) {
    try {
        const arrayBuffer = await blob.arrayBuffer();
        console.log(arrayBuffer);

        const postImage = await fetch(generateTitleBlockURL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/octet-stream',
            },
            body: arrayBuffer,
        });

        const result = await postImage.json();

        const pdfData = {
            name: document.getElementById("toolbar-project-name").value,
            data: {
                bom: this.getBomData(),
                image: result.image,
                power: this.getPowerTableData(),
                titleblock: this.titleBlockModal.savedData,
            },
        };

        const pdfGenerate = await fetch(generatePDF, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(pdfData),
        });

        if (!pdfGenerate.ok){
            throw new Error("Failed to generate PDF");
        }

        const pdfBlob = await pdfGenerate.blob();
        console.log("pdfBlob", pdfBlob);


        return URL.createObjectURL(pdfBlob);
    } catch (error) {
        log("Error in uploadScreenshotAndPreparePDF", error);
        throw error;
    }
}


  /**
   * Retrieves and formats the Bill of Materials (BOM) data.
   * @returns {Array<Object>} An array of formatted BOM parts.
   */
  getBomData() {
    const bomParts = this.experience.bomData.getBomParts().data;

    const formattedBomParts = bomParts.map((part) => {
      const eachPrice = parseFloat(part.each);
      const totalPrice = parseFloat(part.total);

      return {
        ...part,
        each: isNaN(eachPrice) ? "$0.00" : `$${eachPrice.toFixed(2)}`,
        total: isNaN(totalPrice) ? "$0.00" : `$${totalPrice.toFixed(2)}`,
      };
    });
    return formattedBomParts;
  }

  /**
   * Retrieves the power table data.
   * @returns {Array<Object>} An array of power table parts.
   */
  getPowerTableData() {
    return this.experience.powerTableData.getPowerParts().data;
  }
}
